<template>
  <div class="personal">
    <!-- FOR TESTING PURPOSES -->
    <!-- <input type="text" :value="$store.state.authStore.recruit.firstname" @input="handleInput('firstname', $event)" :validator="$v.recruit.firstname" />
    <p v-if="$v.recruit.firstname.$dirty && !$v.recruit.firstname.required">Error</p> -->
    <!-- END OF TESTING -->
    <h2>Personal Details</h2>
    <p class="highlight-text">Please provide your personal details and select the travel centres you wish to be considered for.</p>
    <!-- Form -->
    <b-form>
      <!-- Declaration -->
      <b-form-group>
        <div class="checkbox checkbox-custom">
          <input id="declare" 
            name="declare" 
            type="checkbox" 
            :value="recruit.rightToWork"
            v-model="rightToWork"
            @change="handleInput('rightToWork', $event.target.checked, true)"
          >
          <label for="declare" >
            <strong>I hereby declare that I have the right to work unrestricted and indefinitely in the U.K. or Ireland</strong> (temporary working visas are not sufficient) and that all the details I have provided are correct. I am aware that the submission of false information may lead to dismissal if employed.
          </label>
          <b-alert v-if="$v.recruit.rightToWork.$error" show variant="danger">
            <p>You must tick this box in order to submit the application</p>
          </b-alert>
        </div>
      </b-form-group>
      <!-- Title -->
      <b-form-group
        label="Title"  
      >
        <b-form-select
          :value="recruit.title"
          :options="titleOpts"
          @change="handleInput('title', $event, true)"
          :class="{
            'is-valid': !$v.recruit.title.$error && recruit.title,
            'is-invalid': $v.recruit.title.$error
          }"
        ></b-form-select>
      </b-form-group>
      <!-- First Name -->
      <b-form-group
        label="First Name"
      >
        <b-form-input
          :value="recruit.firstname"
          @input="handleInput('firstname', $event, true)"
          :class="{
            'is-valid': !$v.recruit.firstname.$error && recruit.firstname,
            'is-invalid': $v.recruit.firstname.$error
          }"
        ></b-form-input>
      </b-form-group>
      <!-- Last Name -->
      <b-form-group
        label="Last Name"
      >
        <b-form-input
          :value="recruit.surname"
          @input="handleInput('surname', $event, true)"
          :class="{
            'is-valid': !$v.recruit.surname.$error && recruit.surname,
            'is-invalid': $v.recruit.surname.$error
          }"
        ></b-form-input>
      </b-form-group>
      <!-- Email -->
      <b-form-group
        label="Email"
      >
        <b-form-input
          :value="recruit.email"
          type="email"
          @input="handleInput('email', $event, true)"
          :class="{
            'is-valid': !$v.recruit.email.$error && recruit.email,
            'is-invalid': $v.recruit.email.$error
          }"
        ></b-form-input>
      </b-form-group>
      <!-- Mobile Phone -->
       <div class="mobile-container">
        <b-form-group
        class="country-code"
        label="Code"
      >
        <b-form-input
          type="number"
          :value="recruit.countryCode"
          @input="handleInput('countryCode', $event)"
        ></b-form-input>
      </b-form-group>
      <b-form-group
      class="mobile-number"
        label="Mobile Phone Number"
      >
        <b-form-input
          type="number"
          :value="recruit.phoneMob"
          @input="handleInput('phoneMob', $event, true)"
          :class="{
            'is-valid': !$v.recruit.phoneMob.$error && recruit.phoneMob,
            'is-invalid': $v.recruit.phoneMob.$error
          }"
        ></b-form-input>
      </b-form-group>
       </div>
      <!-- Home Phone -->
      <b-form-group
        label="Home Phone Numbers"
      >
        <b-form-input
          type="number"
          :value="recruit.phoneHome"
          @input="handleInput('phoneHome', $event)"
        ></b-form-input>
      </b-form-group>
      <!-- Address section -->
      <h4 class="my-5">Address Details</h4>
      <p>Please enter your address details below by either searching using the postcode finder or manually typing into the fields below.</p>
      <!-- UK Postcode lookup -->
      <div class="d-flex">
        <div class="mr-4 radio radio-custom"> 
          <input
            type="radio" 
            id="UK-address" 
            value="UK"
            v-model="searchAddressCountry"
            name="searchedCountry"
            @click="handleCountry('UK')"
          />
          <label for="UK-address">UK</label>
        </div>
        <div class="radio radio-custom">
          <input
            type="radio" 
            id="IE-address" 
            value="IE"
            v-model="searchAddressCountry"
            name="searchedCountry"
            @change="handleCountry('IE')"
          />
          <label for="IE-address">Ireland</label>
        </div>
      </div>
      <UkPostcode
        v-if="searchAddressCountry === 'UK'" 
        addressOwner="user"
        :formName="formName"
        labelText="Home Address - Enter postcode"
        placeholderText="Postcode Finder"
      />
      <!-- EIRCODE LOOKUP -->
      <b-form-group v-else-if="searchAddressCountry === 'IE'">
        <Autoaddress
          licencekey="2e9c9e91-eda5-472e-ae24-7d3569bec687"
          vanityMode="true"
          addressProfile="Demo5LineV2"
          country="ie"
          language="en"
          noResultFoundLabel="No search results have been found.Please enter address manually below."
          @onAddressFound="handlePostcode($event)"
          placeHolderLabel="Eircode Finder"
        />
      </b-form-group>
      <!-- Address Line 1 -->
      <b-form-group>
        <b-form-input
          :value="recruit.address1"
          @input="handleInput('address1', $event, true)"
          placeholder="Address Line 1"
          :class="{
            'is-valid': !$v.recruit.address1.$error && recruit.address1,
            'is-invalid': $v.recruit.address1.$error
          }"
        ></b-form-input>
      </b-form-group>
      <!-- Address Line 2 -->
      <b-form-group>
        <b-form-input
          :value="recruit.address2"
          @input="handleInput('address2', $event)"
          placeholder="Address Line 2"
        ></b-form-input>
      </b-form-group>
      <!-- Address Line 3 -->
      <b-form-group>
        <b-form-input
          :value="recruit.address3"
          @input="handleInput('address3', $event)"
          placeholder="Address Line 3"
        ></b-form-input>
      </b-form-group>
      <!-- Address Line 4 -->
      <b-form-group>
        <b-form-input
          :value="recruit.address4"
          @input="handleInput('address4', $event)"
          placeholder="Address Line 4"
        ></b-form-input>
      </b-form-group>
      <!-- Postcode -->
      <b-form-group>
        <b-form-input
          :value="recruit.postcode.toUpperCase()"
          @input="handleInput('postcode', $event, true)"
          :placeholder="referer==='UK' ? 'Postcode' : 'Eircode'"
          :class="{
            'is-valid': !$v.recruit.postcode.$error && recruit.postcode,
            'is-invalid': $v.recruit.postcode.$error
          }"
        ></b-form-input>
      </b-form-group>
      <!-- Which location -->
      <div class="mt-5">
        <p>This is the list of Travel Centres that we're recruiting in, choose which locations you'd like to be considered for.</p>
        <label>If you are flexible and would like to base your choice on where we are recruiting, please click 'Select all UK' or 'Select all Ireland</label>
        <!-- Select all UK -->
        <div class="d-flex align-items-center">
          <b-button
            size="sm"
            class="select-all-btn"
            @click="selectAllCenters('UK')"
          >Select all UK</b-button>
          <b-button
            size="sm"
            class="select-all-btn"
            @click="selectAllCenters('IE')"
          >Select all Ireland</b-button>
        <b-button
            size="sm"
            class="select-all-btn"
            @click="selectAllCenters('unselect')"
          >Unselect all</b-button>
        </div>
        <!-- Check boxes -->
        <div v-if="getTravelCentres && getTravelCentres.length"
              class="radio-checkbox-group"
            >
              <div v-for="(tc, index) in getTravelCentres"
                :key="index"
              >
                <div class="checkbox checkbox-custom" :class="{ disabled: !tc.hiring }">
                  <input type="checkbox"
                    :name="tc.value + 'TC'" 
                    :id="tc.value + 'TC'" 
                    v-model="centres"
                    :value="tc.title"
                    @change="handleInput('centres', $event)"
                    :disabled="!tc.hiring"
                  />
                  <label :for="tc.value + 'TC'">{{ tc.title }}</label>
                  <small v-if="!tc.hiring" class="d-block" style="color: #777; margin-top: -12px;">
                    Not currently recruiting.<br><a href="/jobs/register">Click here</a> to register your interest.
                  </small>
                </div>
              </div>
        </div>
        <b-alert v-if="$v.recruit.centres.$error" show variant="danger">
              <p>You must choose at least one option</p>
        </b-alert>
      </div>
    <!-- <BtnMain @btnClicked="handleProgress" text="Save and progress" btnClass="progress-btn" variant="dark" /> -->
    </b-form>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { updateForm, selectPostcode } from '../../helpers/index'
// import BtnMain from '../../components/BtnMain'
import UkPostcode from '../UkPostcode.vue'
import Autoaddress from '@autoaddress.ie/autoaddress-vue/src/Autoaddress'
import { required, sameAs, email } from 'vuelidate/lib/validators'
export default {
  components: {
    // BtnMain,
    Autoaddress,
    UkPostcode
  },
  computed: {
    ...mapState({recruit: state => state.authStore.recruit}),
    ...mapGetters({
      getRecruit: 'authStore/getRecruit',
      getSectionOpen: 'authStore/getSectionOpen',
      getTravelCentres: 'authStore/getTravelCentres',
      getTravelCentresIreland: 'authStore/getTravelCentresIreland',
      getSelectedCenters: 'authStore/getSelectedCenters'
    }),
    getValidatedState () {
      return this.$v.recruit.$invalid
    }
  },
  data () {
    return {
      titleOpts: [{ text: 'Please select', value: null }, 'Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Other'],
      rightToWork: '',
      travelCentres: '',
      centres: [],
      referer: window.siteVar.referer,
      formName: 'recruit',
      searchAddressCountry: window.siteVar.referer,
    }
  },
  mounted () {
    // Set rightToWork and centres on load - this is required to display checked boxes in accordance with whats saved in mongo
    this.rightToWork = this.recruit.rightToWork
    this.centres = this.recruit.centres
    this.$v.$touch()
  },
  updated () {
    // Pass validation status to vuex
    const validationData = {
      section: 'personal',
      status: !this.$v.recruit.$invalid
    }
    this.updateApplicationCompleteAction(validationData)
  },
  methods: {
    ...mapActions({
      updateRecruitAction: 'authStore/updateRecruitAction',
      sectionOpenAction: 'authStore/sectionOpenAction',
      updateBelfastSelectedAction: 'authStore/updateBelfastSelectedAction',
      selectAddressAction: 'authStore/selectAddressAction',
      setNoJobDropdownAction: 'authStore/setNoJobDropdownAction',
      updateApplicationCompleteAction: 'authStore/updateApplicationCompleteAction',
      setAllUKOrIETravelCentersAction: 'authStore/setAllUKOrIETravelCentersAction'
    }),
    handleInput (input, e, required) {
      if (input === 'centres') {
        this.updateForm(input, this.centres)
      } else {
        this.updateForm(input, e)
      }
      // If field is required run veulidate validation - to apply classes to input not to prevent user from saving
      if (required) {
        this.$v.recruit[input].$touch()
      }
    },
    noVacancyDropdown (index) {
      this.setNoJobDropdownAction(index)
    },
    handlePostcode (address) {
      this.selectPostcode(address)
    },
    selectAllCenters (country) {
      let allCenters = []
      if (country === 'UK') {
        this.getTravelCentres.forEach((item) => {
          if (!this.getTravelCentresIreland.includes(item.title) && item.hiring) {
            allCenters.push(item.title)
          }

        })
      } else if (country === 'IE') {
        this.getTravelCentres.forEach((item) => {
          if (this.getTravelCentresIreland.includes(item.title) && item.hiring) {
            allCenters.push(item.title)
          }
          // include Belfast in IE
          if (item.title === 'Belfast' && item.hiring) {
            allCenters.push(item.title)
          }
        })
      } else if (country === 'unselect') {
        allCenters = []
      }
      this.setAllUKOrIETravelCentersAction(allCenters)
      this.centres = this.getSelectedCenters
    },
    handleCountry(country){
      this.searchAddressCountry = country;
    },
    updateForm,
    selectPostcode
  },
  validations: {
    recruit: {
      rightToWork: {
        sameAs: sameAs(() => true)
      },
      title: {
        required: required
      },
      firstname: {
        required: required
      },
      surname: {
        required: required
      },
      email: {
        required: required,
        email: email
      },
      phoneMob: {
        required: required
      },
      address1: {
        required: required
      },
      postcode: {
        required: required
      },
      centres: {
        required: required
      }
    }
  }
}
</script>

<style scoped>
.postcode-lookup-input {
  position: relative;
}
.postcode-lookup {
  position: relative;
}
.postcode-dropdown {
  background-color: #fff;
  position: absolute;
  width: 100% !important;
  top: 40px !important;
  max-height: 200px;
  z-index: 10;
  border: 1px solid #4da3ff;
  overflow: auto;
}
.select-all-btn {
  margin: 10px 10px 10px 0;
}
.checkbox.disabled {
  margin-bottom: 12px;
}
.checkbox.disabled label {
  cursor: default;
}

.checkbox.disabled label::before{
  display: none;
}

@media only screen and (min-width: 992px) {
  .postcode-dropdown {
    right: 0;
    top: 50px;
    width: 70%;
  }    
  }

.postcode-dropdown ul {
  list-style: none;
    padding: 0;
}

.postcode-dropdown li {
  padding: 10px;
}

.postcode-result {
  transition: all .3s ease;
}

.postcode-result:hover {
  cursor: pointer;
  background-color: #4da3ff;
}

.manual-address {
  display: flex;
  justify-content: center;
}

.disabled-check label::before {
  border-color: #4545451a;
}

@media only screen and (min-width: 767px) {
  .radio-checkbox-group {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
  }

  @media only screen and (min-width: 992px) {
    .radio-checkbox-group {
      display: grid;
    grid-template-columns: repeat(3, 1fr);
    }
  }

.popup a {
  color: red;
}

.invalid-message {
  color: red;
}
.mobile-container {
display: flex;
}

.country-code {
  width: 100px;
  margin-right: 20px;
}

.mobile-number {
  width: 100%;
}

</style>
